import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusOnFirstErrorField } from '../../../hooks/errorFieldFocus';
import { useValidator } from '../../../hooks/formValidator/useValidator';
import Button from '../../../ui/Button';
import useRallyFormValidationSchema from './useValidationSchema';
import useRallyFormState from './useFormState';
import useFormRefs from './useFormRefs';
import { saveAdminRally, updateAdminRally } from '../../../api/admin/rallies';
import ButtonsGroup from '../../../ui/ButtonsGroup';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../../app.config.vars/routes';
import FormFields from './formFields';
import { generateIdRoutePath } from '../../../helpers';
import { getFlatTranslations, handleFileUploadChange, validateRallyFormUpdatedFormFields, } from '../helpers';
import { generateSaveRallyPayload } from '../payloads/saveRallyPayload';
import { generateUpdateRallyPayload } from '../payloads/updateRallyPayload';
import { useAppDispatch } from '../../../store';
import { setIsModalOpen, setModalContent } from '../../../store/slices/global';
import { MODAL_CONTENT } from '../../../app.config.vars/modalContent';
import { validationErrorMessageTranslationKeys } from '../../../hooks/formValidator/validators';
import { buildInputValidationSchema } from './useValidationSchema';
const AdminRallyForm = ({ themes, targetGroups, formType, rallyId }) => {
    const { i18n, t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { formState, updatedFormFields, setUpdatedFormFields, isFormStateReady } = useRallyFormState(rallyId);
    const { inputsValidationSchema, selectsValidationSchema } = useRallyFormValidationSchema({
        isRallyTimeLimit: formState.isRallyTimeLimit,
    });
    const { formErrors, setFormErrors, validateAll, validateSelect, validateInputField } = useValidator({
        inputSchema: inputsValidationSchema,
        selectSchema: selectsValidationSchema,
    });
    const { refs, orderedRefs } = useFormRefs();
    const [submitStatus, setSubmitStatus] = useState(null);
    const [forceFocus, setForceFocus] = useState(false);
    useFocusOnFirstErrorField(formErrors, orderedRefs, forceFocus, setForceFocus);
    const handleBadgeUploadChange = (image) => {
        handleFileUploadChange({
            file: image,
            setFile: formState.setBadge,
            fieldName: 'badge',
            setUpdatedFormFields,
        });
    };
    const handleSubmitError = (error) => {
        setSubmitStatus('error');
        if (error.response.data.error.code === 'invalid_body') {
            setFormErrors((errors) => ({
                ...errors,
                internalTitle: [
                    {
                        messageTranslationKey: validationErrorMessageTranslationKeys.invalidInternalTitle,
                    },
                ],
            }));
            setForceFocus(true);
        }
        else {
            dispatch(setIsModalOpen(true));
            dispatch(setModalContent(MODAL_CONTENT.somethingWentWrong.key));
        }
    };
    const handleCreateRally = async () => {
        const customInputSchema = buildInputValidationSchema(formState.isRallyTimeLimit, !!formState.descriptionTranslations[i18n.language] ||
            !!formState.descriptionTranslations.en ||
            !!formState.descriptionTranslations.de ||
            !!formState.descriptionTranslations.cs);
        const flatTitleTranslations = getFlatTranslations({
            translations: formState.titleTranslations,
            fieldName: 'title',
        });
        const flatDescriptionTranslations = getFlatTranslations({
            translations: formState.descriptionTranslations,
            fieldName: 'description',
        });
        const errors = validateAll({
            inputValues: {
                internalTitle: formState.internalTitle,
                ...flatTitleTranslations,
                ...flatDescriptionTranslations,
                ...(formState.isRallyTimeLimit ? { minutes: formState.minutes } : {}),
            },
            selectValues: {
                themes: !!formState.selectedThemes?.length,
                targetGroups: !!formState.selectedTargetGroups?.length,
            },
            customInputSchema,
        });
        if (Object.keys(errors).length > 0) {
            setForceFocus(true);
            return;
        }
        setSubmitStatus('pending');
        try {
            const res = await saveAdminRally(generateSaveRallyPayload({ formState }));
            setSubmitStatus('success');
            navigate(generateIdRoutePath(APP_ROUTES.adminManageTasks, res.data.uid));
        }
        catch (error) {
            handleSubmitError(error);
        }
    };
    const handleEditRally = async () => {
        if (updatedFormFields.size) {
            const updatedFieldsHasErrors = validateRallyFormUpdatedFormFields({
                updatedFormFields,
                validateInputField,
                validateSelect,
                formState,
                customInputValidationSchema: buildInputValidationSchema(formState.isRallyTimeLimit, !!formState.descriptionTranslations[i18n.language] ||
                    !!formState.descriptionTranslations.en ||
                    !!formState.descriptionTranslations.de ||
                    !!formState.descriptionTranslations.cs),
            });
            if (updatedFieldsHasErrors) {
                setForceFocus(true);
                return;
            }
            setSubmitStatus('pending');
            try {
                await updateAdminRally(rallyId, generateUpdateRallyPayload({
                    formState,
                    updatedFormFields,
                }));
                setSubmitStatus('success');
                navigate(generateIdRoutePath(APP_ROUTES.adminManageTasks, rallyId));
            }
            catch (error) {
                handleSubmitError(error);
            }
        }
        else {
        }
    };
    const returnToRalliesList = () => navigate(APP_ROUTES.adminManageRallies);
    return (_jsxs("form", { className: 'rally-form', onSubmit: (event) => event.preventDefault(), children: [_jsx(FormFields, { themes: themes, targetGroups: targetGroups, handleBadgeUploadChange: handleBadgeUploadChange, refs: refs, formErrors: formErrors, formState: formState, validateSelect: validateSelect, validateInputField: validateInputField, setUpdatedFormFields: setUpdatedFormFields, isFormStateReady: isFormStateReady }), submitStatus === 'error' && _jsx("div", { className: "form-submit-error-message", children: t('') }), _jsx("div", { className: "rally-form-submit", children: _jsxs(ButtonsGroup, { children: [_jsx(Button, { type: "submit", onClick: formType === 'create' ? handleCreateRally : handleEditRally, label: t(formType === 'create' ? 'button.create_rally' : 'button.save_changes'), disabled: submitStatus === 'pending' }), _jsx(Button, { variant: 'outlined', onClick: returnToRalliesList, label: t('button.cancel') })] }) })] }));
};
export default AdminRallyForm;
